import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Target, Building2, ChevronRight, Plus, Clock, AlertTriangle,
  CheckCircle, MapPin, Phone, ArrowRight, FileText, Loader2, Calendar
} from "lucide-react";

const STAGE_COLORS = {
  prospect_identified: "bg-slate-100 text-slate-700", initial_contact: "bg-blue-100 text-blue-700",
  qualified: "bg-indigo-100 text-indigo-700", meeting_scheduled: "bg-violet-100 text-violet-700",
  presented_demo: "bg-purple-100 text-purple-700", verbal_commitment: "bg-amber-100 text-amber-700",
  onboarding_in_progress: "bg-emerald-100 text-emerald-700", live_published: "bg-green-100 text-green-700",
  stalled_lost: "bg-red-100 text-red-700",
};
const TIER_COLORS = { A: "bg-green-500", B: "bg-blue-500", C: "bg-amber-500" };
const TASK_COLORS = { completed: "text-green-500", in_progress: "text-blue-500", pending_venue: "text-amber-500", not_started: "text-slate-300" };

export default function CoordinatorPipeline() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [moveStage, setMoveStage] = useState("");
  const [moveFollowUp, setMoveFollowUp] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newProspect, setNewProspect] = useState({ venue_name: "", venue_type: "Private", municipality: "", address: "", contact_person_name: "", phone: "", email: "", tier: "B", lead_source: "field_outreach", sports_category: [], pain_points: "", estimated_utilization: 50 });

  const fetchAll = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        api.get("/recruitment/pipeline"),
        api.get("/recruitment/config"),
      ]);
      setPipeline(pRes.data);
      setConfig(cRes.data);
    } catch { toast.error("Failed to load pipeline"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createProspect = async () => {
    if (!newProspect.venue_name.trim()) { toast.error("Venue name required"); return; }
    try {
      await api.post("/recruitment/prospects", newProspect);
      toast.success("Prospect added to pipeline!");
      setAddOpen(false);
      setNewProspect({ venue_name: "", venue_type: "Private", municipality: "", address: "", contact_person_name: "", phone: "", email: "", tier: "B", lead_source: "field_outreach", sports_category: [], pain_points: "", estimated_utilization: 50 });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (pid) => {
    try {
      const res = await api.get(`/recruitment/prospects/${pid}`);
      setDetail(res.data);
      setMoveStage(""); setMoveFollowUp(""); setMoveNote("");
    } catch { toast.error("Failed to load"); }
  };

  const handleMove = async () => {
    if (!detail || !moveStage) return;
    try {
      await api.post(`/recruitment/prospects/${detail.prospect_id}/move`, {
        stage: moveStage, next_follow_up_date: moveFollowUp || null, note: moveNote,
      });
      toast.success("Stage updated!");
      openDetail(detail.prospect_id);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Follow-up date required"); }
  };

  const addNote = async () => {
    if (!detail || !newNote.trim()) return;
    try {
      await api.post(`/recruitment/prospects/${detail.prospect_id}/activity`, { activity_type: "note", details: newNote });
      toast.success("Note added");
      setNewNote("");
      openDetail(detail.prospect_id);
    } catch { toast.error("Failed"); }
  };

  const updateTask = async (taskId, status) => {
    if (!detail) return;
    try {
      await api.post(`/recruitment/prospects/${detail.prospect_id}/onboarding`, { task_id: taskId, status });
      openDetail(detail.prospect_id);
      fetchAll();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex justify-center items-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const stages = config?.stages || [];
  const activeStages = stages.filter(s => s.id !== "stalled_lost" && s.id !== "live_published");

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-5" data-testid="coordinator-pipeline">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl flex items-center gap-2"><Target className="w-6 h-6 text-primary" />My Pipeline</h1>
          <p className="text-sm text-muted-foreground">{user?.name} — Field Coordinator</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="rounded-full" data-testid="add-prospect-btn">
          <Plus className="w-4 h-4 mr-1" />Add Prospect
        </Button>
      </div>

      {/* Pipeline Kanban */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-[1200px]">
          {activeStages.map(stage => {
            const data = pipeline?.pipeline?.[stage.id] || { prospects: [], count: 0 };
            return (
              <div key={stage.id} className="w-56 shrink-0">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">{stage.label}</span>
                  <Badge variant="secondary" className="text-[10px] h-5">{data.count}</Badge>
                </div>
                <div className="space-y-2 min-h-[120px]">
                  {data.prospects.map(p => (
                    <div key={p.prospect_id} onClick={() => openDetail(p.prospect_id)}
                      className="bg-white border rounded-lg p-3 cursor-pointer hover:border-primary/50 hover:shadow-md transition-all" data-testid={`prospect-${p.prospect_id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium truncate flex-1">{p.venue_name}</span>
                        <span className={`w-5 h-5 rounded-full text-white text-[9px] flex items-center justify-center font-bold ml-1 ${TIER_COLORS[p.tier] || "bg-slate-400"}`}>{p.tier}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{p.municipality || "—"}</p>
                      {p.contact_person_name && <p className="text-[10px] text-muted-foreground truncate">{p.contact_person_name}</p>}
                      <div className="flex items-center justify-between mt-1.5">
                        {p.next_follow_up_date && <span className="text-[9px] text-blue-600 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{p.next_follow_up_date?.slice(0, 10)}</span>}
                        {p.days_in_stage > 5 && <Badge variant="destructive" className="text-[8px] h-4">{p.days_in_stage}d</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {/* Live + Stalled summary columns */}
          {["live_published", "stalled_lost"].map(sid => {
            const stage = stages.find(s => s.id === sid);
            const data = pipeline?.pipeline?.[sid] || { prospects: [], count: 0 };
            return (
              <div key={sid} className="w-44 shrink-0">
                <div className="flex items-center justify-between mb-2 px-1">
                  <span className="text-[10px] font-heading uppercase tracking-wider text-muted-foreground">{stage?.label}</span>
                  <Badge className={sid === "live_published" ? "bg-green-500 text-white" : "bg-red-500 text-white"} variant="secondary">{data.count}</Badge>
                </div>
                <div className="space-y-1">
                  {data.prospects.slice(0, 4).map(p => (
                    <div key={p.prospect_id} onClick={() => openDetail(p.prospect_id)}
                      className="text-[10px] px-2 py-1.5 border rounded cursor-pointer hover:bg-muted/50 truncate">{p.venue_name}</div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Add Prospect Dialog ── */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New Venue Prospect</DialogTitle>
            <DialogDescription>Add a new venue to your pipeline</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Venue Name *</Label><Input value={newProspect.venue_name} onChange={e => setNewProspect({...newProspect, venue_name: e.target.value})} data-testid="prospect-venue-name" placeholder="e.g. Makati Sports Complex" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Venue Type</Label>
                <Select value={newProspect.venue_type} onValueChange={v => setNewProspect({...newProspect, venue_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["Private","HOA","Barangay","LGU","Corporate"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Tier</Label>
                <Select value={newProspect.tier} onValueChange={v => setNewProspect({...newProspect, tier: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["A","B","C"].map(t => <SelectItem key={t} value={t}>Tier {t}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Municipality / City</Label><Input value={newProspect.municipality} onChange={e => setNewProspect({...newProspect, municipality: e.target.value})} placeholder="e.g. Makati" /></div>
            <div><Label className="text-xs">Address</Label><Input value={newProspect.address} onChange={e => setNewProspect({...newProspect, address: e.target.value})} /></div>
            <Separator />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Person</Label><Input value={newProspect.contact_person_name} onChange={e => setNewProspect({...newProspect, contact_person_name: e.target.value})} /></div>
              <div><Label className="text-xs">Phone</Label><Input value={newProspect.phone} onChange={e => setNewProspect({...newProspect, phone: e.target.value})} /></div>
            </div>
            <div><Label className="text-xs">Email</Label><Input value={newProspect.email} onChange={e => setNewProspect({...newProspect, email: e.target.value})} /></div>
            <Separator />
            <div><Label className="text-xs">Lead Source</Label>
              <Select value={newProspect.lead_source} onValueChange={v => setNewProspect({...newProspect, lead_source: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(config?.lead_sources || []).map(s => <SelectItem key={s} value={s}>{s.replace(/_/g," ")}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-xs">Pain Points / Notes</Label><Textarea value={newProspect.pain_points} onChange={e => setNewProspect({...newProspect, pain_points: e.target.value})} className="text-sm" placeholder="Current booking issues, why they need SetPlay..." /></div>
            <Button onClick={createProspect} className="w-full rounded-full" data-testid="create-prospect-submit">
              <Plus className="w-4 h-4 mr-2" />Add to Pipeline
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Prospect Detail Dialog ── */}
      <Dialog open={!!detail} onOpenChange={v => { if (!v) setDetail(null); }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
                  {detail.venue_name}
                  <Badge className={STAGE_COLORS[detail.current_stage] || "bg-muted"}>{detail.current_stage?.replace(/_/g," ")}</Badge>
                  <Badge className={`${TIER_COLORS[detail.tier]} text-white`}>Tier {detail.tier}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground text-xs">Type:</span> {detail.venue_type}</div>
                  <div><span className="text-muted-foreground text-xs">Municipality:</span> {detail.municipality || "—"}</div>
                  <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-muted-foreground" />{detail.phone || "—"}</div>
                  <div><span className="text-muted-foreground text-xs">Contact:</span> {detail.contact_person_name || "—"} ({detail.contact_person_role})</div>
                  <div><span className="text-muted-foreground text-xs">Utilization:</span> {detail.estimated_utilization}%</div>
                  <div><span className="text-muted-foreground text-xs">Source:</span> {detail.lead_source?.replace(/_/g," ")}</div>
                  {detail.pain_points && <div className="col-span-2"><span className="text-muted-foreground text-xs">Pain Points:</span> {detail.pain_points}</div>}
                </div>

                <Separator />

                {/* Move Stage */}
                <div>
                  <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Move to Stage</p>
                  <div className="flex gap-2 flex-wrap items-end">
                    <Select value={moveStage} onValueChange={setMoveStage}>
                      <SelectTrigger className="w-48 h-9"><SelectValue placeholder="Select stage" /></SelectTrigger>
                      <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <div>
                      <Label className="text-[10px]">Follow-up Date</Label>
                      <Input type="date" value={moveFollowUp} onChange={e => setMoveFollowUp(e.target.value)} className="w-36 h-9" />
                    </div>
                    <Button size="sm" onClick={handleMove} disabled={!moveStage} data-testid="move-stage-btn">
                      <ArrowRight className="w-3 h-3 mr-1" />Move
                    </Button>
                  </div>
                  {moveStage && <Input value={moveNote} onChange={e => setMoveNote(e.target.value)} placeholder="Add a note about this stage change..." className="mt-2 text-sm" />}
                </div>

                {/* Onboarding Checklist */}
                {detail.current_stage === "onboarding_in_progress" && (
                  <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground">Onboarding Checklist</p>
                        <span className="text-sm font-bold text-primary">{detail.onboarding_progress}%</span>
                      </div>
                      <div className="h-2 bg-muted rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-primary rounded-full transition-all" style={{width:`${detail.onboarding_progress}%`}} />
                      </div>
                      <div className="space-y-2">
                        {(detail.onboarding_checklist || []).map(task => {
                          const status = detail.onboarding_tasks?.[task.task_id] || "not_started";
                          return (
                            <div key={task.task_id} className="flex items-center justify-between py-1 border-b last:border-0">
                              <div className="flex items-center gap-2">
                                <CheckCircle className={`w-4 h-4 ${TASK_COLORS[status]}`} />
                                <span className="text-sm">{task.label}</span>
                              </div>
                              <Select value={status} onValueChange={v => updateTask(task.task_id, v)}>
                                <SelectTrigger className="w-32 h-7 text-[10px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="not_started">Not Started</SelectItem>
                                  <SelectItem value="in_progress">In Progress</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="pending_venue">Pending Venue</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                <Separator />

                {/* Add Note */}
                <div>
                  <p className="text-xs font-heading uppercase tracking-wider text-muted-foreground mb-2">Activity Log</p>
                  <div className="flex gap-2 mb-3">
                    <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note, call summary, meeting update..." className="text-sm" data-testid="add-note-input" />
                    <Button size="sm" onClick={addNote} disabled={!newNote.trim()} data-testid="add-note-btn">
                      <FileText className="w-3 h-3 mr-1" />Log
                    </Button>
                  </div>
                  {(detail.activities || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-2">No activity yet</p>
                  ) : (
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {detail.activities.map(a => (
                        <div key={a.activity_id} className="text-xs border-l-2 border-primary/30 pl-2 py-1">
                          <div className="flex justify-between">
                            <span className="font-medium capitalize">{a.activity_type?.replace(/_/g," ")}</span>
                            <span className="text-muted-foreground">{a.timestamp?.slice(0,10)}</span>
                          </div>
                          <p className="text-muted-foreground">{a.details}</p>
                          <p className="text-[10px] text-muted-foreground">by {a.coordinator_name}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
